import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import PageTableHeader from "../../../../components/pageTableHeader/pageTableHeader";
import CommonTable from "../../../../components/table/table";
import DefaultButton from "../../../../components/buttons/defaultButton";
import {
  getPayrollDashboard,
  getPayrollRuns,
  getPendingApprovals
} from "../../../../../services/workspace/payrollManagement.service";
import {
  FiDollarSign,
  FiTrendingUp,
  FiCreditCard,
  FiUsers,
  FiMinusCircle,
  FiPlay,
  FiFileText,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiAlertCircle
} from "react-icons/fi";
import toast from "react-hot-toast";
import PayrollRunWizard from "../PayrollRunWizard/page";


const items = [{ label: "Payroll Dashboard", path: "" }];

const PayrollDashboard = () => {
  const navigate = useNavigate();
  const { limit } = useSelector((state) => state.global);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [runsData, setRunsData] = useState([]);
  const [approvalsData, setApprovalsData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [approvalsPage, setApprovalsPage] = useState(1);
  const [showWizard, setShowWizard] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    fetchRuns();
    fetchPendingApprovals();
  }, [currentPage, limit]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await getPayrollDashboard({});
      setDashboardData(response);
    } catch (error) {
      console.error("Dashboard fetch error:", error);
      toast.error(error?.response?.data?.message || "Failed to fetch dashboard data");
      // Set default empty data on error
      setDashboardData({
        kpis: {
          total_gross: 0,
          total_net: 0,
          bank_debit: 0,
          total_deductions: 0,
          employee_count: 0
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchRuns = async () => {
    try {
      const response = await getPayrollRuns({
        page: currentPage,
        limit: limit,
      });
      // Ensure runsData is always an array
      const runs = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
        ? response
        : [];
      setRunsData(runs);
    } catch (error) {
      console.error("Runs fetch error:", error);
      toast.error(error?.response?.data?.message || "Failed to fetch payroll runs");
      setRunsData([]);
    }
  };

  const fetchPendingApprovals = async () => {
    try {
      const response = await getPendingApprovals({});
      // Ensure approvalsData is always an array
      const approvals = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
        ? response
        : [];
      setApprovalsData(approvals);
    } catch (error) {
      console.error("Approvals fetch error:", error);
      toast.error(error?.response?.data?.message || "Failed to fetch pending approvals");
      setApprovalsData([]);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "—";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const getStatusBadge = (status) => {
    const normalizedStatus = String(status || "DRAFT").toUpperCase();
    const statusConfig = {
      DRAFT: { color: "bg-gray-100 text-gray-700", label: "Draft" },
      PROCESSING: { color: "bg-blue-100 text-blue-700", label: "Processing" },
      DRY_COMPLETED: { color: "bg-yellow-100 text-yellow-700", label: "Dry Run Completed" },
      READY_FOR_FINALIZE: { color: "bg-purple-100 text-purple-700", label: "Ready to Finalize" },
      PENDING_APPROVAL: { color: "bg-amber-100 text-amber-700", label: "Pending Approval" },
      FINALIZING: { color: "bg-indigo-100 text-indigo-700", label: "Finalizing" },
      COMPLETED: { color: "bg-success-100 text-success-700", label: "Completed" },
      APPROVED: { color: "bg-green-100 text-green-700", label: "Approved" },
      FAILED: { color: "bg-error-100 text-error-700", label: "Failed" },
      ROLLED_BACK: { color: "bg-orange-100 text-orange-700", label: "Rolled Back" },
    };
    const config = statusConfig[normalizedStatus] || statusConfig.DRAFT;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getRunTypeBadge = (runType) => {
    return runType === "DRY" ? (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
        Dry Run
      </span>
    ) : (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
        Final
      </span>
    );
  };

  const handleViewRun = (runId) => {
    navigate(`/hr/payroll-runs/${runId}`);
  };

  const handleStartPayrollRun = () => {
    setShowWizard(true);
  };

  const handleWizardComplete = (runId) => {
    setShowWizard(false);
    navigate(`/hr/payroll-runs/${runId}`);
  };

  const runsTableData = (Array.isArray(runsData) ? runsData : []).map((run) => {
    const currentStatus = String(run?.status || "DRAFT").toUpperCase();

    return {
      id: run.id,
      period: {
        data: run.period,
        variant: "text",
      },
      run_type: {
        data: getRunTypeBadge(run.run_type),
        variant: "text",
      },
      status: {
        data: getStatusBadge(currentStatus),
        variant: "text",
      },
      initiated_by: {
        data: run.initiated_by,
        variant: "text",
      },
      created_at: {
        data: formatDate(run.created_at),
        variant: "text",
      },
      actions: {
        data: (
          <div className="flex gap-2">
            <button
              onClick={() => handleViewRun(run.uuid)}
              className="text-theme hover:text-themeHover text-sm font-medium"
            >
              View
            </button>
            {currentStatus === "FAILED" && (
            <button
              onClick={async () => {
                try {
                  toast.loading("Retrying payroll run...");
                  const { retryPayrollRun } = await import("../../../../../services/workspace/payrollManagement.service");
                  await retryPayrollRun(run.uuid);
                  toast.dismiss();
                  toast.success("Payroll run retried successfully!");
                  fetchRuns();
                } catch (error) {
                  toast.dismiss();
                  toast.error(error?.response?.data?.message || "Failed to retry payroll run");
                }
              }}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              Retry
            </button>
            )}
            {currentStatus === "COMPLETED" && (
            <button
              onClick={() => navigate(`/hr/payroll-runs/${run.uuid}`)}
              className="text-green-600 hover:text-green-700 text-sm font-medium"
            >
              Download
            </button>
            )}
            {(currentStatus === "PENDING_APPROVAL" || currentStatus === "READY_FOR_FINALIZE" || currentStatus === "DRY_COMPLETED") && (
            <>
              <button
                onClick={async () => {
                  try {
                    toast.loading("Approving payroll run...");
                    const { approvePayrollRun } = await import("../../../../../services/workspace/payrollManagement.service");
                    await approvePayrollRun(run.uuid, { comment: "Approved for finalization" });
                    toast.dismiss();
                    toast.success("Payroll run approved successfully!");
                    fetchRuns();
                  } catch (error) {
                    toast.dismiss();
                    toast.error(error?.response?.data?.message || "Failed to approve payroll run");
                  }
                }}
                className="text-green-600 hover:text-green-700 text-sm font-medium"
              >
                Approve
              </button>
              <button
                onClick={async () => {
                  const reason = prompt("Please enter rejection reason:");
                  if (!reason || !reason.trim()) {
                    toast.error("Rejection reason is required");
                    return;
                  }
                  try {
                    toast.loading("Rejecting payroll run...");
                    const { rejectPayrollRun } = await import("../../../../../services/workspace/payrollManagement.service");
                    await rejectPayrollRun(run.uuid, { reason });
                    toast.dismiss();
                    toast.success("Payroll run rejected");
                    fetchRuns();
                  } catch (error) {
                    toast.dismiss();
                    toast.error(error?.response?.data?.message || "Failed to reject payroll run");
                  }
                }}
                className="text-red-600 hover:text-red-700 text-sm font-medium"
              >
                Reject
              </button>
            </>
            )}
          </div>
        ),
        variant: "text",
      },
    };
  });

  const approvalsTableData = (Array.isArray(approvalsData) ? approvalsData : []).map((approval) => ({
    id: approval.id,
    period: {
      data: approval.period,
      variant: "text",
    },
    run_type: {
      data: getRunTypeBadge(approval.run_type),
      variant: "text",
    },
    status: {
      data: getStatusBadge(approval.status),
      variant: "text",
    },
    initiated_by: {
      data: approval.initiated_by,
      variant: "text",
    },
    exception_count: {
      data: approval.exception_count > 0 ? (
        <span className="text-error-600 font-medium">{approval.exception_count} issues</span>
      ) : (
        <span className="text-success-600">No issues</span>
      ),
      variant: "text",
    },
    actions: {
      data: (
        <div className="flex gap-2">
          <button
            onClick={() => handleViewRun(approval.id)}
            className="text-theme hover:text-themeHover text-sm font-medium"
          >
            View Exceptions
          </button>
          <button
            onClick={async () => {
              try {
                toast.loading("Approving payroll run...");
                const { approvePayrollRun } = await import("../../../../../services/workspace/payrollManagement.service");
                await approvePayrollRun(approval.id, { comment: "Approved for finalization" });
                toast.dismiss();
                toast.success("Payroll run approved successfully!");
                fetchPendingApprovals();
                fetchRuns();
              } catch (error) {
                toast.dismiss();
                toast.error(error?.response?.data?.message || "Failed to approve payroll run");
              }
            }}
            className="text-success-600 hover:text-success-700 text-sm font-medium"
          >
            Approve
          </button>
          <button
            onClick={async () => {
              const reason = prompt("Please enter rejection reason:");
              if (!reason) return;
              try {
                toast.loading("Rejecting payroll run...");
                const { rejectPayrollRun } = await import("../../../../../services/workspace/payrollManagement.service");
                await rejectPayrollRun(approval.id, { reason });
                toast.dismiss();
                toast.success("Payroll run rejected");
                fetchPendingApprovals();
                fetchRuns();
              } catch (error) {
                toast.dismiss();
                toast.error(error?.response?.data?.message || "Failed to reject payroll run");
              }
            }}
            className="text-error-600 hover:text-error-700 text-sm font-medium"
          >
            Reject
          </button>
        </div>
      ),
      variant: "text",
    },
  }));

  const runsTableHeaders = [
    { label: "Period", key: "period" },
    { label: "Run Type", key: "run_type" },
    { label: "Status", key: "status" },
    { label: "Initiated By", key: "initiated_by" },
    { label: "Created At", key: "created_at" },
    { label: "Actions", key: "actions" },
  ];

  const approvalsTableHeaders = [
    { label: "Period", key: "period" },
    { label: "Run Type", key: "run_type" },
    { label: "Status", key: "status" },
    { label: "Initiated By", key: "initiated_by" },
    { label: "Exceptions", key: "exception_count" },
    { label: "Actions", key: "actions" },
  ];

  if (loading) {
    return (
      <div className="flex flex-col gap-4">
        <PageTableHeader
          isSearch={false}
          filter={false}
          shorting={false}
          breadcrumbs={items}
        />
        {/* Skeleton KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            "border-l-blue-500",
            "border-l-green-500",
            "border-l-purple-500",
            "border-l-red-500",
            "border-l-indigo-500",
          ].map((accent, i) => (
            <div
              key={i}
              className={`bg-white p-5 rounded-xl border border-borderColor border-l-[3px] ${accent} animate-pulse`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 space-y-3">
                  <div className="h-3 w-20 bg-gray-200 rounded" />
                  <div className="h-7 w-24 bg-gray-200 rounded" />
                </div>
                <div className="w-11 h-11 rounded-full bg-gray-100" />
              </div>
            </div>
          ))}
        </div>
        {/* Skeleton Quick Actions */}
        <div className="animate-pulse">
          <div className="h-5 w-28 bg-gray-200 rounded mb-3" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-lg">
            {[1, 2].map((i) => (
              <div key={i} className="bg-white p-5 rounded-xl border border-borderColor">
                <div className="w-10 h-10 rounded-full bg-gray-100 mb-3" />
                <div className="h-4 w-28 bg-gray-200 rounded mb-2" />
                <div className="h-3 w-36 bg-gray-100 rounded" />
              </div>
            ))}
          </div>
        </div>
        {/* Skeleton Table */}
        <div className="bg-white p-5 rounded-xl border border-borderColor animate-pulse">
          <div className="h-5 w-24 bg-gray-200 rounded mb-4" />
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-12 bg-gray-50 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const quickActions = [
    {
      title: "Start Payroll Run",
      description: "Initiate a new dry or final payroll run",
      icon: <FiPlay className="text-xl text-white" />,
      primary: true,
      onClick: handleStartPayrollRun,
    },
    {
      title: "View Reports",
      description: "Browse statutory and payroll reports",
      icon: <FiFileText className="text-xl text-indigo-600" />,
      iconBg: "bg-indigo-50",
      onClick: () => navigate("/hr/statutory-reports"),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <PageTableHeader
        isSearch={false}
        filter={false}
        shorting={false}
        breadcrumbs={items}
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <KPICard
          icon={<FiDollarSign className="text-xl" />}
          label="Total Gross"
          value={formatCurrency(dashboardData?.kpis?.total_gross)}
          color="text-blue-600"
          bgColor="bg-blue-50"
          ringColor="ring-blue-100"
          accentColor="border-l-blue-500"
        />
        <KPICard
          icon={<FiTrendingUp className="text-xl" />}
          label="Total Net"
          value={formatCurrency(dashboardData?.kpis?.total_net)}
          color="text-green-600"
          bgColor="bg-green-50"
          ringColor="ring-green-100"
          accentColor="border-l-green-500"
        />
        <KPICard
          icon={<FiCreditCard className="text-xl" />}
          label="Bank Debit"
          value={formatCurrency(dashboardData?.kpis?.bank_debit)}
          color="text-purple-600"
          bgColor="bg-purple-50"
          ringColor="ring-purple-100"
          accentColor="border-l-purple-500"
        />
        <KPICard
          icon={<FiMinusCircle className="text-xl" />}
          label="Total Deductions"
          value={formatCurrency(dashboardData?.kpis?.total_deductions)}
          color="text-red-600"
          bgColor="bg-red-50"
          ringColor="ring-red-100"
          accentColor="border-l-red-500"
        />
        <KPICard
          icon={<FiUsers className="text-xl" />}
          label="Employee Count"
          value={dashboardData?.kpis?.employee_count || 0}
          color="text-indigo-600"
          bgColor="bg-indigo-50"
          ringColor="ring-indigo-100"
          accentColor="border-l-indigo-500"
        />
      </div>

      {/* Quick Actions */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-lg">
          {quickActions.map((action, index) => (
            <div
              key={index}
              onClick={action.onClick}
              className={`p-5 rounded-xl cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-md ${
                action.primary
                  ? "bg-gradient-to-br from-primary-600 to-primary-700 text-white"
                  : "bg-white border border-borderColor hover:border-gray-200"
              }`}
            >
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center mb-3 ${
                  action.primary
                    ? "bg-white/20"
                    : action.iconBg || "bg-gray-50"
                }`}
              >
                {action.icon}
              </div>
              <p
                className={`font-semibold text-sm ${
                  action.primary ? "text-white" : "text-gray-900"
                }`}
              >
                {action.title}
              </p>
              <p
                className={`text-xs mt-1 ${
                  action.primary ? "text-white/80" : "text-gray-500"
                }`}
              >
                {action.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Latest Runs */}
      <div className="bg-white p-5 rounded-xl border border-borderColor">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Latest Runs</h3>
        <CommonTable
          TableHeaderData={runsTableHeaders}
          tabledata={runsTableData}
          currentPage={currentPage}
          totalData={runsData.length}
          setCurrentPage={setCurrentPage}
          loading={loading}
          action={{}}
        />
      </div>

      {/* Pending Approvals */}
      {approvalsData.length > 0 && (
        <div className="bg-white p-5 rounded-xl border border-borderColor">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Pending Approvals</h3>
            <span className="px-2.5 py-1 bg-amber-50 text-amber-600 rounded-full text-xs font-medium">
              {approvalsData.length} pending
            </span>
          </div>
          <CommonTable
            TableHeaderData={approvalsTableHeaders}
            tabledata={approvalsTableData}
            currentPage={approvalsPage}
            totalData={approvalsData.length}
            setCurrentPage={setApprovalsPage}
            loading={loading}
            action={{}}
          />
        </div>
      )}

      {/* Payroll Run Wizard */}
      {showWizard && (
        <PayrollRunWizard
          onClose={() => setShowWizard(false)}
          onComplete={handleWizardComplete}
        />
      )}
    </div>
  );
};

const KPICard = ({ icon, label, value, color, bgColor, ringColor, accentColor }) => {
  return (
    <div className={`bg-white p-5 rounded-xl border border-borderColor border-l-[3px] ${accentColor} hover:shadow-sm transition-shadow`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm text-gray-500 mb-2">{label}</p>
          <p className={`text-2xl font-bold text-gray-900`}>{value}</p>
        </div>
        <div className={`w-11 h-11 rounded-full ${bgColor} ring-4 ${ringColor} flex items-center justify-center`}>
          <div className={color}>{icon}</div>
        </div>
      </div>
    </div>
  );
};

export default PayrollDashboard;
